import { ReactElement } from "react";
import {
  Paper,
  Typography,
  Button,
  Box,
  CircularProgress,
} from "@mui/material";
import { HourglassEmpty as HourglassIcon } from "@mui/icons-material";

export interface WaitingScreenProps {
  title?: string;
  message?: string;
  instruction?: string;
  estimatedTime?: string;
  onCancel?: () => void;
  cancelButtonText?: string;
  className?: string;
}

const WaitingScreen = ({
  title = "Processing",
  message = "Your data is being processed",
  instruction = "You're free to browse around while your data runs!",
  estimatedTime = "up to 10 min",
  onCancel,
  cancelButtonText = "Cancel analysis",
  className = "",
}: WaitingScreenProps): ReactElement => {
  return (
    <Paper
      elevation={2}
      className={`p-8 text-center ${className}`}
      sx={{
        borderRadius: "12px",
        border: "1px solid #e0e0e0",
        backgroundColor: "white",
        position: "relative",
      }}
    >
      {/* Hourglass Icon */}
      <Box className="mb-6">
        <HourglassIcon
          sx={{
            fontSize: 80,
            color: "#19C1DB",
            animation: "spin 2s linear infinite",
            "@keyframes spin": {
              "0%": {
                transform: "rotate(0deg)",
              },
              "100%": {
                transform: "rotate(360deg)",
              },
            },
          }}
        />
      </Box>

      {/* Title */}
      <Typography
        variant="h5"
        className="text-gray-900 mb-4"
        sx={{ fontWeight: 600 }}
      >
        {title}
      </Typography>

      {/* Main Message */}
      <Typography
        variant="h6"
        className="text-gray-700 mb-2"
        sx={{ fontWeight: 500 }}
      >
        {message} ({estimatedTime}).
      </Typography>

      {/* Instruction */}
      <Typography
        variant="body1"
        className="text-gray-600 mb-8"
        sx={{ fontSize: "1rem", lineHeight: 1.6 }}
      >
        {instruction}
      </Typography>

      {/* Progress Indicator */}
      <Box className="mb-8">
        <CircularProgress
          size={40}
          thickness={4}
          sx={{
            color: "#19C1DB",
            mb: 2,
          }}
        />
        <Typography variant="body2" className="text-gray-500">
          Processing in progress...
        </Typography>
      </Box>

      {/* Cancel Button */}
      {onCancel && (
        <Button
          variant="contained"
          onClick={onCancel}
          className="bg-red-500 hover:bg-red-600 text-white"
          sx={{
            textTransform: "none",
            fontWeight: 500,
            px: 4,
            py: 1.5,
            borderRadius: "8px",
          }}
        >
          {cancelButtonText}
        </Button>
      )}
    </Paper>
  );
};

export default WaitingScreen;
